import React from "react";
import {
  IconButton,
  Card,
  MenuItem,
  ClickAwayListener,
  Typography,
  Divider,
  makeStyles
} from "@material-ui/core";
import ExitToAppOutlined from "@material-ui/icons/ExitToAppOutlined";
import SyncAltOutlined from "@material-ui/icons/SyncAltOutlined";

function formatDocName(doc) {
  return doc
    .split(/[_\s]+/)        // split on "_" or spaces (one or more)
    .map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
}


const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: "relative",
    display: "inline-flex"
  },

  /* Dropdown position — UNDER navbar & avatar */
  paper: {
    position: "absolute",
    top: "calc(100% + 1px)", // 👈 pops just below avatar
    right: 0,
    zIndex: 1300
  },

  card: {
    width: 280,
    minHeight: 252,
    borderRadius: 4,
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.16)",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
    fontFamily: '"Montserrat", sans-serif !important',
    "& *": {
      fontFamily: '"Montserrat", sans-serif !important',
    }
  },


  name: {
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "18px",
    letterSpacing: 0,
    color: "#3A3A42"
  },
  email: {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "10px",
    letterSpacing: 0,
    color: "#3A3A42",
    marginTop: 8
  },
  role: {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "10px",
    letterSpacing: 0,
    color: "#3A3A42",
    marginTop: 10
  },

  profileBlock: {
    backgroundColor: "#E3F2FD",
    minHeight: 144,
  },
  
  header: {
    padding: "24px 16px 12px",
    paddingBottom: 0
  },
  
  companyRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px 12px"
  },
  
  /* Divider should NOT span full width */
  insetDivider: {
    margin: "16px 16px 0px",
    backgroundColor: "#99CCDF"
  },

  divider: {
    backgroundColor: "#D0E4F2"
  },

  companyLabel: {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "10px",
    letterSpacing: 0,
    color: "#6E6E72"
  },
  companyName: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "12px",
    letterSpacing: 0,
    marginLeft: 4,
    color: "#3A3A42"
  },
  editIcon: {
    fontSize: 16,
    color: "#0E6A8C"
  },
  companyActionBtn: {
    width: 28,
    height: 28,
    borderRadius: 24,
    padding: 6,
    color: "#0E6A8C"
  },

  /* ---------- MENU ---------- */
  menuItem: {
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "12px",
    letterSpacing: 0,
    color: "#3A3A42",
    minHeight: 36,
    padding: "8px 16px",
    "&:hover": {
      backgroundColor: "#F3F8FC"
    }
  },
  signOutIcon: {
    fontSize: 20,
    marginRight: 12,
    color: "#0F0F19"
  },
  signOutMenuItem: {
    borderTop: "1px solid #EBEBF0"
  },

  /* ---------- AVATAR ---------- */
  avatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "#E5E7EB",
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#1F2933",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }
}));

const UserProfileDropdown = ({
  initials,
  name,
  email,
  role = "Admin",
  company,
  onEditCompany,
  onManageUsers,
  onManageCompany,
  onLogout
}) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className={classes.wrapper}>
        <IconButton
          disableRipple
          onClick={() => setOpen((prev) => !prev)}
        >
          <div className={classes.avatar}>{initials}</div>
        </IconButton>

        {open && (
          <div className={classes.paper}>
            <Card className={classes.card}>
              {/* Profile + Company block (same background) */}
              <div className={classes.profileBlock}>
                {/* Header */}
                <div className={classes.header}>
                  <Typography className={classes.name}>{name}</Typography>
                  <Typography className={classes.email}>{email}</Typography>
                  <Typography className={classes.role}>{role}</Typography>
                </div>

                {/* Inset divider */}
                <Divider className={classes.insetDivider} />

                {/* Company */}
                <div className={classes.companyRow}>
                  <Typography>
                    <span className={classes.companyLabel}>Company :</span>
                    <span className={classes.companyName}>{formatDocName(company)}</span>
                  </Typography>
                  <IconButton
                    className={classes.companyActionBtn}
                    onClick={onEditCompany}
                    disableRipple
                  >
                    <SyncAltOutlined className={classes.editIcon} />
                  </IconButton>
                </div>
              </div>

              {/* Actions */}
              <MenuItem
                className={classes.menuItem}
                onClick={onManageUsers}
              >
                Manage Users
              </MenuItem>

              <MenuItem
                className={classes.menuItem}
                onClick={onManageCompany}
              >
                Manage Company
              </MenuItem>

              <MenuItem
                className={`${classes.menuItem} ${classes.signOutMenuItem}`}
                onClick={onLogout}
              >
                <ExitToAppOutlined className={classes.signOutIcon} />
                Sign Out
              </MenuItem>
            </Card>
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default UserProfileDropdown;
